import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { filter } from 'rxjs/internal/operators/filter';
import { skip } from 'rxjs/operators';
import {
  ServiceProviderHeaderRowItemSearchResult,
  ServiceProviderType,
} from '../../../../core/api/mhsConfigurationApiClient';
import { NfzBranchLookup } from '../../../../core/context/nhf-branch-lookup';
import { UsertStore } from '../../../../core/context/userStore';
import { ServiceProvidersService } from '../../services/service-providers-service';

interface SpFilters {
  query: string;
  type: ServiceProviderType | null;
}

@Component({
  selector: 'app-service-providers-view',
  imports: [
    CommonModule,
    FormsModule,
    NzTableModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzFlexModule,
    NzDropdownModule,
    NzTagModule,
    NzModalModule,
  ],
  templateUrl: './service-providers-view.html',
  styleUrl: './service-providers-view.scss',
})
export class ServiceProvidersViewComponent {
  readonly typeOptions: { label: string; value: ServiceProviderType | null }[] = [
    { label: 'Wszystkie', value: null },
    { label: 'NFZ', value: ServiceProviderType.Nhf },
    { label: 'Prywatny', value: ServiceProviderType.Private },
  ];

  serviceProvidersService = inject(ServiceProvidersService);
  tenantStore = inject(UsertStore);
  router = inject(Router);
  messageService = inject(NzModalService);
  searchResult = signal<ServiceProviderHeaderRowItemSearchResult | null>(null);
  filters = signal<SpFilters>({ query: '', type: null });
  loading = signal<boolean>(true);
  nfzBranchCodes = [{ code: '', name: 'Brak' }, ...NfzBranchLookup.branches];
  total = 1;
  pageSize = 10;
  pageIndex = 1;

  constructor() {
    toObservable(this.filters)
      .pipe(skip(1), debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        this.onQueryParamsChange({
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          sort: [],
          filter: [
            { key: 'query', value: value.query },
            { key: 'type', value: value.type },
          ],
        });
      });

    this.tenantStore.currentTenant$
      .pipe(
        skip(1),
        filter((tenant) => tenant !== null),
        distinctUntilChanged(),
      )
      .subscribe(() => this.reload());
  }

  reload(): void {
    this.total = 1;
    this.pageIndex = 1;
    this.filters.update((f) => ({ ...f, query: '' }));
    this.searchResult.set(null);
  }

  queryChange(value: string): void {
    this.filters.update((f) => ({ ...f, query: value }));
  }

  setType(type: ServiceProviderType | null): void {
    this.filters.update((f) => ({ ...f, type }));
  }

  currentTypeLabel(): string {
    return this.typeOptions.find((o) => o.value === this.filters().type)?.label ?? 'Wszystkie';
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    this.loading.set(true);
    const { pageSize, pageIndex, sort, filter } = params;
    const currentSort = sort.find((item) => item.value !== null);
    const sortField = currentSort?.key;
    const sortOrder = currentSort?.value ?? null;
    const phrase = filter.find((item) => item.key === 'query')?.value ?? '';
    const type = filter.find((item) => item.key === 'type')?.value ?? null;
    const isOrderByAsc = sortOrder === 'ascend';

    this.serviceProvidersService
      .search(pageIndex, pageSize, phrase, type ?? undefined, sortField, isOrderByAsc)
      .subscribe({
        next: (result) => {
          this.searchResult.set(result);
          this.total = result.total ?? 0;
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error during service provider search:', error);
          this.loading.set(false);
        },
      });
  }
  detailsClick(serviceProviderId: number): void {
    this.router.navigate(['/service-providers', serviceProviderId, 'details']);
  }
  createServiceProviderClick(): void {
    this.router.navigate(['/service-providers', 'create']);
  }
  editServiceProviderClick(serviceProviderId: number): void {
    this.router.navigate(['/service-providers', serviceProviderId, 'edit']);
  }
  unhideServiceProviderClick(serviceProviderId: number): void {
    this.messageService.confirm({
      nzTitle: 'Czy na pewno chcesz odkryć podmiot?',
      nzContent: 'Podmiot zostanie odkryty i będzie widoczny w systemie.',
      nzOkText: 'Tak, odkryj',
      nzCancelText: 'Anuluj',
      nzOkDanger: true,
      nzOnOk: () => {
        this.unhideServiceProvider(serviceProviderId);
      },
    });
  }
  hideServiceProviderClick(serviceProviderId: number): void {
    this.messageService.confirm({
      nzTitle: 'Czy na pewno chcesz ukryć podmiot?',
      nzContent: 'Podmiot zostanie ukryty i nie będzie widoczny w systemie.',
      nzOkText: 'Tak, ukryj',
      nzCancelText: 'Anuluj',
      nzOkDanger: true,
      nzOnOk: () => {
        this.hideServiceProvider(serviceProviderId);
      },
    });
  }
  hideServiceProvider(serviceProviderId: number): void {
    this.loading.set(true);
    this.serviceProvidersService.hide(serviceProviderId).subscribe(() => {
      this.reload();
      this.loading.set(false);
    });
  }
  unhideServiceProvider(serviceProviderId: number): void {
    this.loading.set(true);
    this.serviceProvidersService.reveal(serviceProviderId).subscribe(() => {
      this.reload();
      this.loading.set(false);
    });
  }
}
